import { toast } from '@Shelly/shelly-design-system';
import { get } from 'svelte/store';
import { postReq } from 'src/services/http';
import { format } from 'svelte-i18n';
import { getDevicesWithItsChannels, real_id } from './interface_helpers';
import { devices } from 'src/stores/lists/devices';
import type { Device } from 'src/types/Device';
import type { sharedToList } from 'src/types/requests/services/get_shared';

export interface SharedChannelData {
	deviceId: string;
	users: string[];
}

/**
 * Removes shared users from a device
 * @param sharedUsers - Array of user emails to remove
 * @param deviceId - The device ID to unshare
 */
async function removeSharedDevice(sharedUsers: string[], deviceId: string): Promise<void> {
	for (const userEmail of sharedUsers) {
		const removeSharedRes = await postReq(`/device/delete_shared`, {
			id: deviceId,
			email: userEmail,
		});

		if (removeSharedRes?.isok) {
			toast.open({
				type: 'success',
				title:
					get(format)('shared_device_remove_user', {
						values: { email: userEmail },
					}) || `You successfully removed ${userEmail} from this device.`,
			});
		}
	}
}

/**
 * Checks all device channels for shared users and collects the data
 * @param device - The device to check
 * @returns Array of shared channels data
 */
export const checkSharedChannels = async (device: Device): Promise<SharedChannelData[]> => {
	const deviceIds = getDevicesWithItsChannels(device);
	const sharedChannelsData: SharedChannelData[] = [];
	const devicesStore = get(devices);

	for (const deviceId of deviceIds) {
		const channelDevice = devicesStore[deviceId];
		const channelNumber = channelDevice?.channel;

		if (channelNumber === undefined) {
			continue;
		}

		const resShared = await postReq(`/device/get_shared`, {
			id: real_id(deviceId),
			channel: channelNumber,
		});

		if (resShared?.isok) {
			const data = await (resShared as sharedToList).data;
			if (data?.users && data.users.length > 0) {
				const actualDeviceId = channelDevice?.id ?? deviceId;
				sharedChannelsData.push({
					deviceId: actualDeviceId,
					users: data.users,
				});
			}
		}
	}

	return sharedChannelsData;
};

/**
 * Unshares all channels that have shared users
 * @param sharedChannelsData - Array of channel data with shared users
 */
export const unshareAllChannels = async (sharedChannelsData: SharedChannelData[]): Promise<void> => {
	if (sharedChannelsData.length > 0) {
		for (const channelData of sharedChannelsData) {
			await removeSharedDevice(channelData.users, channelData.deviceId);
		}
	}
};
